<script setup lang="ts">
import { computed, defineAsyncComponent, watch } from 'vue';
import ApiTraceWindow from '@/common/components/organisms/apiTraceWindow/ApiTraceWindow.vue';
import { useApiTraceEvent } from '@/common/components/organisms/apiTraceWindow/apiTraceWindow.uses';
import LoadingScreen from '@/common/components/organisms/loading/LoadingScreen.vue';
import NoticeWindow from '@/common/components/organisms/noticeWindow/NoticeWindow.vue';
import chatView from '@/ai/views/ChatView.vue';
import OnSiteAlarm from '@/common/components/organisms/onSiteAlarm/OnSiteAlarm.vue';
import { PRODUCT_THEMES } from '@/common/utils';
import { useMessageEventInMainThread } from '@/common/utils/windowMessage.util';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import { useViewManagerStore } from './common/stores/view-manager';
import { useMyInfoStore } from '@/config/stores/my-info';
import { useChatbotConfigStore } from '@/config/stores/chatbotConfig';
import { useAiAssistantVisible } from '@/ai/composables/useAiAssistantVisible';

const UserEnvDevTool = import.meta.env.DEV
  ? defineAsyncComponent(
      () => import('@/common/components/development/userEnvDevTool/UserEnvDevTool.vue'),
    )
  : null;

const ScreenXRayOverlay = import.meta.env.DEV
  ? defineAsyncComponent(
      () => import('@/common/components/development/screenXRay/ScreenXRayOverlay.vue'),
    )
  : null;

const { isLoadingRouterForEach } = storeToRefs(useViewManagerStore());
const route = useRoute();

const { isShow: isShowApiTraceWindow } = useApiTraceEvent();
useMessageEventInMainThread();
const { myInfo: userInfo } = storeToRefs(useMyInfoStore());

const excludedPaths = ['/', '/login'];
const chatbotConfigStore = useChatbotConfigStore();
// 로그인 후 1회 fetch — settings.enabled 가 false 면 launcher 자체를 숨김.
// fetch 실패 시 기본값(enabled=true) 유지하므로 UX 영향 없음.
watch(
  () => userInfo.value?.userId,
  (uid, prev) => {
    if (uid && uid !== -1 && uid !== prev) {
      chatbotConfigStore.fetchSettings().catch(() => undefined);
    }
  },
  { immediate: true },
);
const isAiAssistantVisible = useAiAssistantVisible();
const showChatView = computed(
  () =>
    !isLoadingRouterForEach.value &&
    !excludedPaths.includes(route.path) &&
    isAiAssistantVisible.value,
);

const rootEl = document.documentElement;

const setThemeDataset = (theme?: string) => {
  const userTheme = theme?.toLowerCase() ?? '';

  rootEl.dataset.theme = PRODUCT_THEMES.includes(userTheme as (typeof PRODUCT_THEMES)[number])
    ? userTheme
    : 'light';
};

watch(
  [() => userInfo.value.theme, () => route.path],
  ([newTheme, newPath]) => {
    const isLogin = newPath.includes('login') || newPath === '/';

    const themeToSet = isLogin ? 'light' : newTheme;
    setThemeDataset(themeToSet);
  },
  { immediate: true },
);

const setLanguageDataset = (language?: string) => {
  rootEl.dataset.language = language ?? 'EN';
};

watch(
  () => userInfo.value.language,
  (newLang) => setLanguageDataset(newLang),
  { immediate: true },
);
</script>

<template>
  <router-view v-slot="{ Component }">
    <loading-screen v-if="isLoadingRouterForEach" />
    <component
      :is="Component"
      v-else
    />
  </router-view>
  <!--
    chatbot view 항상 노출. develop 의 `propertyData.useAI` 가드는 챗봇 데모 흐름에서
    의도적으로 제거되어 있다. 옵션 on/off 기능이 도입되면 이 자리에 다시 `v-if` 가드를 건다.
  -->
  <chat-view v-if="showChatView" />
  <on-site-alarm />
  <api-trace-window v-model:is-show="isShowApiTraceWindow" />
  <!--  <reload-prompt />-->
  <notice-window />
  <vue-query-devtools />
  <user-env-dev-tool v-if="UserEnvDevTool" />
  <screen-x-ray-overlay v-if="ScreenXRayOverlay" />
</template>

<style lang="scss">
/*
 * 챗봇이 우측에 docked 된 상태에서 메인 콘텐츠가 가려지지 않도록 body 우측 padding 부여.
 * .chatbot-docked 토글 + --chatbot-dock-width CSS 변수는 ChatWindow.vue 가 갱신.
 * 좌측 GNB / 사이드바는 영향 없음 (대부분 fixed left:0 이라 padding-right 와 무관).
 */
:root {
  --chatbot-dock-width: 0;
}
html.chatbot-docked body {
  padding-right: var(--chatbot-dock-width);
  transition: padding-right 0.18s ease;
}

/*
 * SlideOutWindow 와의 겹침 처리.
 *   - 챗봇 docked 영역은 항상 가장 위에 보여야 함 (사용자 요구).
 *   - 슬라이드는 `left: 100% + translateX(-100%)` 로 우측 정렬되어 viewport 우측 끝에
 *     붙는다. 챗봇 dock 너비만큼 추가로 좌측 이동시켜 슬라이드 우측 가장자리가 챗봇 좌측
 *     가장자리에 닿도록 한다. `slide-out-window--right` 외에도 `slide-out--show` 상태일 때
 *     line 351 의 transform 이 적용되므로 같은 selector 로 override.
 *   - 슬라이드의 max-width 도 chatbot 영역 빼서 cap → 좌측이 viewport 밖으로 안 나감.
 *   - GNB expand/opened 등 다른 상태에서는 transform 이 다른 selector 로 셋되지만 그건
 *     좌측 슬라이드(--left) 이고 우측 슬라이드(--right)에는 영향 없으므로 추가 처리 불필요.
 */
html.chatbot-docked .slide-out-window--right,
html.chatbot-docked .slide-out-window {
  max-width: calc(100vw - var(--chatbot-dock-width)) !important;
}
html.chatbot-docked .slide-out--show .slide-out-window {
  transform: translateX(calc(-100% - var(--chatbot-dock-width))) !important;
}

/*
 * slide-out--expand (슬라이드 펼침) 상태에서 슬라이드 width 가 viewport 거의 가득. 이 경우
 * chatbot dock 만큼 더 좁혀야 챗봇 영역 침범 안 함. GNB 상태별 width calc 4가지 모두 -dock 추가.
 */
html.chatbot-docked .slide-out--expand .slide-out-window {
  width: calc(
    100% -
      (var(--gnb-menu-width) + var(--slide-out-expand-button-width) + var(--chatbot-dock-width))
  ) !important;
}
html.chatbot-docked .slide-out--gnb-expanded.slide-out--expand .slide-out-window {
  width: calc(
    100% -
      (
        var(--gnb-menu-width-expanded) + var(--slide-out-expand-button-width) +
          var(--chatbot-dock-width)
      )
  ) !important;
}
html.chatbot-docked .slide-out--gnb-opened.slide-out--expand .slide-out-window {
  width: calc(
    100% -
      (
        var(--gnb-menu-width-opened) + var(--slide-out-expand-button-width) +
          var(--chatbot-dock-width)
      )
  ) !important;
}
html.chatbot-docked .slide-out--gnb-expanded-and-opened.slide-out--expand .slide-out-window {
  width: calc(
    100% -
      (
        var(--gnb-menu-width-expanded-and-opened) + var(--slide-out-expand-button-width) +
          var(--chatbot-dock-width)
      )
  ) !important;
}

/*
 * 풀폭 모달 — 사용자 알람 '룰 설정'(.alert-rule-window) 은 position:fixed + left 고정 +
 * width:100%-여백 이라 body padding-right 를 안 따라가 챗봇 dock 밑으로 깔린다.
 * dock 너비만큼 width 를 줄여 우측 끝이 챗봇 좌측 가장자리에 닿게 한다.
 * (--horizontal-padding 은 .alert-rule-window 자체에 정의됨. 중앙정렬 소형 모달은 미해당.)
 */
html.chatbot-docked .alert-rule-window {
  width: calc(100% - (var(--horizontal-padding) * 2) - var(--chatbot-dock-width)) !important;
}

/* TanStack Query DevTools floating 버튼이 챗봇과 겹치지 않게 */
html.chatbot-docked .tsqd-open-btn-container {
  right: calc(var(--chatbot-dock-width) + 16px) !important;
}
</style>
